import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    FormControlLabel,
    Checkbox,
    Grid,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import HrmsImage from "../../assets/Hrms.png";
import axios from "axios";
import { axiosInstance, localAxios } from "../../Axios/axios";
import { Loader } from '../../components/loade';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { setCredentials } from "../../redux/authSlice";
import { useDispatch } from "react-redux";


const LoginPage = ({ onLogin }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch(); 
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [ipAddress, setIpAddress] = useState("");

    useEffect(() => {
        const savedEmail = localStorage.getItem("rememberedEmail");
        const savedPassword = localStorage.getItem("rememberedPassword");
        if (savedEmail && savedPassword) {
            setEmail(savedEmail);
            setPassword(savedPassword);
            setRememberMe(true);
        }
        fetchIpAddress();
    }, []);

    const fetchIpAddress = async () => {
        try {
            const response = await axios.get('https://api.ipify.org?format=json');
            const ip = response.data.ip;
            setIpAddress(ip);
            console.log("User IP Address:", ip);
        } catch (error) {
            console.error("Error fetching IP address:", error);
        }
    };

    const handleLogin = async () => {
        if (!email || !email.includes('@') || !email.includes('.')) {
            setFormError("Please enter a valid email address.");
            setIsLoading(false);
            return;
        }

        if (!email || !password) {
            setFormError("Please enter both User ID and Password");
            return;
        }

        setFormError("");
        setIsLoading(true);
        const startTime = Date.now();

        const data = {
            email: email.toLowerCase(),
            password: password
        };

        try {
            const response = await axiosInstance(localAxios("/organization/userLogin", data));

            if (response.data.message) {
                const { accessToken, refreshToken, user, organizationId } = response.data.message;

                if (accessToken) {
                    // 1. Store the accessToken in the Redux state
                    dispatch(setCredentials({ accessToken }));
                    
                    // 2. Store tokens and IP in localStorage (as before)
                    // localStorage.setItem("accessToken", accessToken);                  
                    localStorage.setItem("userIpAddress", ipAddress);

                    // Decode token to get essential IDs (this remains as requested)
                    try {
                        const tokenPayload = accessToken.split('.')[1];
                        const decodedPayload = JSON.parse(atob(tokenPayload));
                        localStorage.setItem("userId", decodedPayload.userId);
                        localStorage.setItem("organizationId", decodedPayload.organizationId);
                    } catch (tokenError) {
                        console.error("Error decoding token:", tokenError);
                    }
                }

                if (user) {
                    

                    // Remaining essential user data saved to localStorage:
                    localStorage.setItem("userId", user.id);
                    localStorage.setItem("organizationId", user.organization_id);
                    localStorage.setItem("userType", user.user_type);
                    localStorage.setItem('reportingmanagerId', user.report_manager_id);
                    localStorage.setItem("locationId", user.location_id);
                    localStorage.setItem("isSubscribed", user.is_subscribed);

                    if (rememberMe) {
                        localStorage.setItem("rememberedEmail", email);
                        localStorage.setItem("rememberedPassword", password);
                    } else {
                        localStorage.removeItem("rememberedEmail");
                        localStorage.removeItem("rememberedPassword");
                    }

                    // Call onLogin if provided by parent
                    if (onLogin) {
                        onLogin({
                            id: user.id,
                            email: user.email,
                            userType: user.user_type,
                        });
                    }

                    const elapsedTime = Date.now() - startTime;
                    const remainingTime = Math.max(0, 5000 - elapsedTime);

                    setTimeout(() => {
                        setIsLoading(false);
                        const userType = user.user_type;
                        // Navigation logic remains the same
                        if (userType === 'Hr') {
                            navigate('/HR');
                        } else if (userType === 'Manager' ) {
                            navigate('/Manager');
                        } else if (userType === 'Employee') {
                            navigate('/Employee');
                        } else if (userType === 'Team Lead') {
                            navigate('/TeamLead');
                        }
                    }, remainingTime);

                    return;
                }
            }
        } catch (error) {
            console.error("Login Error:", error.response ? error.response.data : error.message);

            if (error.response && error.response.data && error.response.data.message) {
                setFormError(error.response.data.message);
            } else {
                setFormError("Login failed. Please check your credentials and try again.");
            }

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 5000 - elapsedTime);

            setTimeout(() => {
                setIsLoading(false);
            }, remainingTime);
        }
    };

    const handleRegisterClick = () => {
        navigate('/register');
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                height: "100vh",
                width: "100vw",
                backgroundColor: "rgba(41, 41, 41, 1)",
                position: "relative",
            }}
        >
            {isLoading && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        zIndex: 1000,
                        backdropFilter: "blur(3px)",
                    }}
                >
                    <Loader />
                </Box>
            )}
            <Box
                sx={{
                    flex: 1,
                    backgroundImage: `url(${HrmsImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                }}
            ></Box>
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: { xs: 3, md: 4 },
                }}
            >
                <Box
                    sx={{
                        width: { xs: "90%", md: "60%" },
                        maxWidth: "350px",
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: "bold",
                            mb: 2,
                            color: "#ffffff",
                            textAlign: "center",
                        }}
                    >
                        Account Login
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 3,
                            color: "#bbbbbb",
                            textAlign: "center",
                        }}
                    >
                        If you are already a member you can login with your user
                        ID and password.
                    </Typography>

                    {formError && (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                color: "#ff6b6b",
                                textAlign: "center",
                                padding: "8px",
                                backgroundColor: "rgba(255, 107, 107, 0.1)",
                                borderRadius: "4px",
                            }}
                        >
                            {formError}
                        </Typography>
                    )}
                    <form>
                        <TextField
                            fullWidth
                            placeholder="User ID"
                            variant="outlined"
                            size="small"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{
                                backgroundColor: "#ffffff",
                                borderRadius: "4px",
                                mb: 2,
                            }}
                            InputProps={{
                                style: { color: "#000000" },
                            }}
                        />
                        <TextField
                            fullWidth
                            placeholder="Password"
                            variant="outlined"
                            size="small"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={{
                                backgroundColor: "#ffffff",
                                borderRadius: "4px",
                                mb: 2,
                            }}
                            InputProps={{
                                style: { color: "#000000" },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    sx={{
                                        color: "#ffffff",
                                        "&.Mui-checked": {
                                            color: "#ffffff",
                                        },
                                    }}
                                />
                            }
                            label={<Typography variant="caption" sx={{ color: "#bbbbbb" }}>Remember me</Typography>}
                            sx={{ mb: 2 }}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleLogin}
                                    disabled={isLoading}
                                    sx={{
                                        backgroundColor: "#1a237e",
                                        color: "#fff",
                                        padding: "8px",
                                        fontWeight: "bold",
                                        fontSize: "0.85rem",
                                        "&:hover": {
                                            backgroundColor: "#0d47a1",
                                        },
                                        "&.Mui-disabled": {
                                            backgroundColor: "#344195",
                                            color: "#ffffffaa",
                                        }
                                    }}
                                >
                                    {isLoading ? "Logging in..." : "Login"}
                                </Button>
                            </Grid>
                        </Grid>

                        <Grid container justifyContent="space-between" sx={{ mt: 2 }}>
                            <Grid item>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "#bbbbbb",
                                    }}
                                >
                                    <a
                                        href="#"
                                        style={{ color: "#bbbbbb", textDecoration: "none" }}
                                        onClick={() => navigate("/forgot-password")}
                                    >
                                        Forgot Password?
                                    </a>
                                </Typography>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Box>
        </Box>
    );
};

export default LoginPage;